import { ImgComparisonSlider } from '@img-comparison-slider/react';
import { useNavigate } from '@tanstack/react-router';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ChevronsLeftRight, ImagePlus, Paintbrush } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { getEstate } from '@/features/estates-property/api/estate';
import { getProject } from '@/features/projects/api/projects';
import { useAiRenderingCanGenerate } from '@/features/renderings/ai-rendering/hooks/use-ai-rendering-can-generate';
import { useAiRenderingCreate } from '@/features/renderings/ai-rendering/hooks/use-ai-rendering-create';
import { useRenderingsMobileNavigation } from '@/features/renderings/hooks/use-renderings-mobile-navigation';
import { RenderingsMobileHeader } from '@/features/renderings/layout/renderings-mobile-header';
import { RenderingsMobilePreviewActions } from '@/features/renderings/layout/renderings-mobile-preview-actions';
import { RenderingsSidebar } from '@/features/renderings/layout/renderings-sidebar';
import { magicEditBrushSizeAtom } from '@/features/renderings/magic-edit/atoms/brush-size';
import { magicEditModeAtom } from '@/features/renderings/magic-edit/atoms/mode';
import { magicEditPromptAtom } from '@/features/renderings/magic-edit/atoms/prompt';
import {
  BrushCanvas,
  type BrushCanvasHandle,
  type BrushCanvasHistory,
} from '@/features/renderings/magic-edit/components/brush-canvas';
import {
  MagicEditToolbar,
  magicEditToolbarSelectedButtonClass,
  magicEditToolbarToggleButtonClass,
} from '@/features/renderings/magic-edit/components/magic-edit-toolbar';
import { MagicEditInstructions } from '@/features/renderings/magic-edit/components/magic-edit-instructions';
import { MagicEditMaskHintBanner } from '@/features/renderings/magic-edit/components/magic-edit-mask-hint-banner';
import { useMagicEditCreate } from '@/features/renderings/magic-edit/hooks/use-magic-edit-create';
import { useMagicEditMaskHint } from '@/features/renderings/magic-edit/hooks/use-magic-edit-mask-hint';
import { renderingsActiveToolAtom } from '@/features/renderings/shared/atoms/active-tool';
import {
  type RenderingImage,
  renderingsCurrentImageAtom,
  renderingsSelectedImageAtom,
  renderingsSelectedPreviewImageAtom,
  renderingsUploadedImagesAtom,
} from '@/features/renderings/shared/atoms/image-files';
import {
  renderingsEstateIdAtom,
  renderingsProjectIdAtom,
} from '@/features/renderings/shared/atoms/relation';
import { GeneratedImagesSection } from '@/features/renderings/shared/components/generated-images-section';
import useResizeObserver from '@/hooks/use-resize-observer';
import { cn } from '@/lib/utils';
import { createTempId } from '@/utils/tempId';
import { addWatermark } from '@/utils/watermark';
import { watermarkAtom } from '@/features/image-rendering/atoms/settings';
import { usePostHog } from 'posthog-js/react';

type BrushTool = 'brush' | 'eraser';
type RenderedView = 'compare' | 'brush';

const DEFAULT_BRUSH_HISTORY: BrushCanvasHistory = {
  canUndo: false,
  hasMask: false,
};

const PROGRESS_EXPECTED_MS = 90_000;

const toDownloadFileName = (name: string) => {
  const normalized = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalized || 'rendering';
};

const imageTypeToExtension = (type?: string) => {
  if (!type) return undefined;

  const extensionByType: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
  };

  return extensionByType[type.toLowerCase()];
};

const getImageExtension = (image: RenderingImage, href: string) => {
  const fromType = imageTypeToExtension(image.file?.type || image.fileType);
  if (fromType) return fromType;

  const path = href.split(/[?#]/)[0];
  const match = path.match(/\.([a-z0-9]+)$/i);

  return match?.[1]?.toLowerCase() || 'png';
};

const PROGRESS_TICK_MS = 500;
const PROGRESS_HIDE_DELAY_MS = 250;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const useBrushState = () => {
  const brushRef = useRef<BrushCanvasHandle | null>(null);
  const [tool, setTool] = useState<BrushTool>('brush');
  const [history, setHistory] = useState<BrushCanvasHistory>(DEFAULT_BRUSH_HISTORY);

  const clearBrush = useCallback(() => {
    brushRef.current?.clear();
  }, []);

  const resetBrush = useCallback(() => {
    brushRef.current?.clear();
    setHistory(DEFAULT_BRUSH_HISTORY);
  }, []);

  return {
    brushRef,
    tool,
    setTool,
    history,
    setHistory,
    clearBrush,
    resetBrush,
  };
};

const useFakeProgress = (isLoading: boolean) => {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const intervalRef = useRef<number | null>(null);

  const clearProgressInterval = useCallback(() => {
    if (intervalRef.current === null) return;

    window.clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, []);

  useEffect(() => {
    clearProgressInterval();

    if (!isLoading) return;

    setProgress(0);
    setIsVisible(true);

    const start = performance.now();

    const tick = () => {
      const elapsedMs = performance.now() - start;
      const ratio = Math.min(1, elapsedMs / PROGRESS_EXPECTED_MS);
      const earlyCap = 70;
      const lateCap = 94;
      const dynamicCap = earlyCap + (lateCap - earlyCap) * ratio;
      const ideal = 5 + (dynamicCap - 5) * ratio;

      setProgress((prev) => {
        const safePrev = Number.isFinite(prev) ? prev : 0;
        const target = Math.max(safePrev, ideal);
        const delta = target - safePrev;

        if (delta <= 0.01) return safePrev;

        return safePrev + Math.min(delta, 0.4);
      });
    };

    tick();
    intervalRef.current = window.setInterval(tick, PROGRESS_TICK_MS);

    return clearProgressInterval;
  }, [clearProgressInterval, isLoading]);

  useEffect(() => {
    if (isLoading || !isVisible) return;

    clearProgressInterval();
    setProgress(100);

    const timeout = window.setTimeout(() => {
      setIsVisible(false);
      setProgress(0);
    }, PROGRESS_HIDE_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [clearProgressInterval, isLoading, isVisible]);

  return {
    progress,
    isVisible,
  };
};

const useImageUpload = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const setSelectedImage = useSetAtom(renderingsSelectedImageAtom);
  const setSelectedPreviewImage = useSetAtom(renderingsSelectedPreviewImageAtom);
  const setUploadedImages = useSetAtom(renderingsUploadedImagesAtom);

  const setImageFile = useCallback(
    (file: File) => {
      const next: RenderingImage = {
        id: createTempId(),
        originalImage: URL.createObjectURL(file),
        fileType: file.type,
        file,
      };

      setSelectedImage(next);
      setSelectedPreviewImage(next);
      setUploadedImages((prev) => [...prev, next]);
    },
    [setSelectedImage, setSelectedPreviewImage, setUploadedImages]
  );

  const openFilePicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  return {
    inputRef,
    setImageFile,
    openFilePicker,
  };
};

const useBrushColor = (magicEditMode: string) => {
  return useMemo(() => {
    if (typeof window === 'undefined') {
      return magicEditMode === 'remove' ? 'rgba(239,68,68,0.35)' : 'rgba(7,180,192,0.35)';
    }

    const styles = getComputedStyle(document.documentElement);

    const hslToRgba = (hsl: string, alpha: number) => {
      const parts = hsl.trim().split(/\s+/);

      if (parts.length < 3) return `rgba(0,0,0,${alpha})`;

      const [h, s, l] = parts;
      return `hsla(${h}, ${s}, ${l}, ${alpha})`;
    };

    const alpha = 0.35;

    if (magicEditMode === 'remove') {
      const danger = styles.getPropertyValue('--danger-color');
      return hslToRgba(danger || '0 84% 60%', alpha);
    }

    const primary = styles.getPropertyValue('--primary-color-light');
    return hslToRgba(primary || '190 90% 45%', alpha);
  }, [magicEditMode]);
};

const RenderingProgress = ({ progress, text }: { progress: number; text: string }) => {
  const roundedProgress = Math.round(progress);

  return (
    <div className="flex h-full w-full p-6 text-sm text-gray-700">
      <div className="flex h-full w-full flex-col items-center justify-center rounded-[5px] border border-dashed border-primary-light bg-primary-light/5 px-6 py-10">
        <div className="w-full max-w-[480px]">
          <div className="mb-4 text-center text-base font-semibold text-text-primary">{text}</div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-primary-light/20">
            <div
              className="h-full rounded-full bg-primary-light transition-[width] duration-300"
              style={{ width: `${roundedProgress}%` }}
            />
          </div>
          <div className="mt-2 text-center text-xs text-gray-600">{roundedProgress}%</div>
        </div>
      </div>
    </div>
  );
};

const UploadArea = ({
  inputRef,
  onFileSelect,
  onOpenFilePicker,
}: {
  inputRef: React.Ref<HTMLInputElement>;
  onFileSelect: (file: File) => void;
  onOpenFilePicker: () => void;
}) => {
  const { t } = useTranslation();

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0];

    if (file) onFileSelect(file);

    e.target.value = '';
  };

  const handleDrop: React.DragEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();

    const file = e.dataTransfer.files?.[0];

    if (file?.type.startsWith('image/')) onFileSelect(file);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;

    e.preventDefault();
    onOpenFilePicker();
  };

  return (
    <Button
      type="button"
      variant="ghost"
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      onClick={onOpenFilePicker}
      onKeyDown={handleKeyDown}
      className={cn(
        'flex h-full w-full cursor-pointer flex-col items-center justify-center bg-white',
        'box-border border-2 border-transparent hover:border-primary-light focus-visible:border-primary-light focus-visible:outline-none'
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={handleInputChange}
      />
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="flex size-14 items-center justify-center rounded-full bg-slate-50">
          <ImagePlus aria-hidden className="size-7 text-slate-500" />
        </div>
        <div className="text-sm font-semibold text-slate-600">{t('main-rendering-area')}</div>
        <div className="text-xs text-slate-500">{t('click-to-upload-image')}</div>
      </div>
    </Button>
  );
};

export const AiRenderingPage = () => {
  const posthog = usePostHog();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobileView = !useResizeObserver(640);

  const currentImage = useAtomValue(renderingsCurrentImageAtom);
  const [activeTool, setActiveTool] = useAtom(renderingsActiveToolAtom);
  const [selectedImage, setSelectedImage] = useAtom(renderingsSelectedImageAtom);
  const [uploadedImages, setUploadedImages] = useAtom(renderingsUploadedImagesAtom);
  const setSelectedPreviewImage = useSetAtom(renderingsSelectedPreviewImageAtom);
  const [brushSize, setBrushSize] = useAtom(magicEditBrushSizeAtom);
  const magicEditMode = useAtomValue(magicEditModeAtom);
  const [magicEditPrompt, setMagicEditPrompt] = useAtom(magicEditPromptAtom);
  const estateId = useAtomValue(renderingsEstateIdAtom);
  const projectId = useAtomValue(renderingsProjectIdAtom);
  const watermarkInfo = useAtomValue(watermarkAtom);

  const [renderedView, setRenderedView] = useState<RenderedView>('compare');
  const [objectName, setObjectName] = useState('');

  const brush = useBrushState();
  const maskHint = useMagicEditMaskHint();
  const upload = useImageUpload();
  const brushColor = useBrushColor(magicEditMode);
  const { createMagicEdit, isLoading: isMagicEditCreating } = useMagicEditCreate();
  const { start: startAiRendering, isLoading: isAiRenderingLoading } = useAiRenderingCreate({
    estateId,
    projectId,
  });
  const canGenerateAiRendering = useAiRenderingCanGenerate();

  const magicEditProgress = useFakeProgress(isMagicEditCreating);
  const aiRenderingProgress = useFakeProgress(isAiRenderingLoading);

  const hasSelectedSource =
    selectedImage &&
    !selectedImage.renderedImage &&
    uploadedImages.some((image) => image.id === selectedImage.id);
  const generatedImages = [
    ...uploadedImages,
    ...(selectedImage && !selectedImage.renderedImage && !hasSelectedSource ? [selectedImage] : []),
  ];

  const magicEditEnabled = activeTool === 'magic-edit' && Boolean(currentImage?.originalImage);
  const canSwitchRenderedView = Boolean(currentImage?.renderedImage);
  const brushCanvasEnabled =
    magicEditEnabled && (!currentImage?.renderedImage || renderedView === 'brush');
  const isRenderingInProgress =
    isAiRenderingLoading ||
    isMagicEditCreating ||
    aiRenderingProgress.isVisible ||
    magicEditProgress.isVisible;
  const showMagicEditToolbars =
    magicEditEnabled && !isRenderingInProgress && (canSwitchRenderedView || brushCanvasEnabled);
  const magicEditImageTopPadding = isMobileView && showMagicEditToolbars ? 'pt-16' : 'pt-6';
  const previewImageContainerClass = cn(
    'flex w-full items-start justify-center pb-6',
    isMobileView ? 'px-0' : 'px-6',
    magicEditImageTopPadding
  );
  const hasRenderedResult = uploadedImages.some((image) => Boolean(image.renderedImage));
  const mobileNav = useRenderingsMobileNavigation({
    isMobileView,
    isRenderingInProgress,
    hasRenderedResult,
  });
  const previewImageMaxHeight = isMobileView
    ? mobileNav.showMobilePreviewChrome
      ? 'calc(100vh - 280px)'
      : 'calc(100vh - 180px)'
    : 'calc(100vh - 310px)';
  const showBrushCanvas =
    brushCanvasEnabled && (!isMobileView || mobileNav.showMobilePreviewChrome);
  const hasSelectedObject = Boolean(estateId || projectId);
  const canAttemptMagicEdit = Boolean(selectedImage) && brushCanvasEnabled;
  const showMaskHintBanner =
    showBrushCanvas && Boolean(currentImage) && !brush.history.hasMask && !maskHint.isDismissed;

  const showProgress = magicEditProgress.isVisible || aiRenderingProgress.isVisible;
  const progress = aiRenderingProgress.isVisible
    ? aiRenderingProgress.progress
    : magicEditProgress.progress;
  const progressText = aiRenderingProgress.isVisible
    ? t('ai-rendering-generating')
    : t('magic-edit-generating');

  const handleMagicEditCreate = useCallback(async () => {
    if (!selectedImage || !brushCanvasEnabled) return;

    if (!hasSelectedObject) {
      toast.warning(t('renderings.select-estate-or-project'));
      return;
    }

    if (!magicEditPrompt.trim()) {
      toast.warning(t('magic-edit-prompt-required'));
      return;
    }

    if (!brush.history.hasMask) {
      toast.warning(t('magic-edit-draw-mask-hint'));
      return;
    }

    const mask = await brush.brushRef.current?.exportMaskPng();

    if (!mask) return;
    posthog.capture('magic-edit-created', {
      mode: magicEditMode,
      estateId,
      projectId,
    });

    await createMagicEdit({
      image: selectedImage,
      mask,
      mode: magicEditMode,
      prompt: magicEditPrompt.trim(),
      estateId,
      projectId,
    });

    brush.clearBrush();
    setMagicEditPrompt('');
  }, [
    brush,
    brushCanvasEnabled,
    createMagicEdit,
    estateId,
    hasSelectedObject,
    magicEditMode,
    magicEditPrompt,
    projectId,
    selectedImage,
    setMagicEditPrompt,
    t,
    posthog,
  ]);

  const handlePreviewCreate = useCallback(() => {
    if (activeTool === 'ai-rendering') {
      startAiRendering();
      return;
    }

    void handleMagicEditCreate();
  }, [activeTool, handleMagicEditCreate, startAiRendering]);

  const previewCreateDisabled =
    activeTool === 'ai-rendering' ? !canGenerateAiRendering : !canAttemptMagicEdit;

  const previewCreateLoading =
    activeTool === 'ai-rendering' ? isAiRenderingLoading : isMagicEditCreating;

  const showRenderedBrush = () => {
    setActiveTool('magic-edit');
    setRenderedView('brush');
  };

  const showRenderedCompare = () => {
    brush.resetBrush();
    setRenderedView('compare');
  };

  const handleUseAsSource = (image: RenderingImage) => {
    if (!image.renderedImage) return;

    const next: RenderingImage = {
      ...image,
      id: createTempId(),
      originalImage: image.renderedImage,
      renderedImage: undefined,
      renderedImageId: undefined,
      magicEditMode: undefined,
      renderingType: undefined,
    };

    setSelectedImage(next);
    setSelectedPreviewImage(next);
    setUploadedImages((prev) => {
      if (prev.some((uploadedImage) => uploadedImage.id === next.id)) return prev;

      const sourceIndex = prev.findIndex((uploadedImage) => uploadedImage.id === image.id);
      if (sourceIndex === -1) return [...prev, next];

      return [...prev.slice(0, sourceIndex + 1), next, ...prev.slice(sourceIndex + 1)];
    });
    toast.info(t('image-set-as-input-for-next-rendering'));
  };

  const handleDownloadImage = async (image: RenderingImage) => {
    const href = image.renderedImage || image.originalImage;

    if (!href) {
      toast.error(t('image-download-failed'));
      return;
    }

    const extension = getImageExtension(image, href);

    const renderedImages = uploadedImages.filter((uploadedImage) =>
      Boolean(uploadedImage.renderedImage)
    );

    const generatedIndex = renderedImages.findIndex(
      (uploadedImage) => uploadedImage.id === image.id
    );

    const fileIndex = generatedIndex >= 0 ? generatedIndex + 1 : 1;

    const baseFileName = toDownloadFileName(objectName);

    const fileName = `${baseFileName}-${fileIndex}.${extension}`;

    const link = document.createElement('a');
    const downloadUrl =
      watermarkInfo.applyWatermark && !image.watermarkApplied
        ? await addWatermark(href, watermarkInfo.text, watermarkInfo.position)
        : href;

    link.href = downloadUrl;
    link.download = fileName;
    link.rel = 'noopener';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEditImage = (image: RenderingImage) => {
    const mediaId = image.renderedImageId;

    if (!mediaId) {
      toast.error(t('image-editor-media-missing'));
      return;
    }

    if (estateId) {
      navigate({
        to: '/estates/$estateId/media/edit/$mediaId',
        params: { estateId, mediaId },
      });
      return;
    }

    if (projectId) {
      navigate({
        to: '/projects/$projectId/media/edit/$mediaId',
        params: { projectId, mediaId },
      });
      return;
    }

    toast.error(t('image-editor-object-missing'));
  };

  useEffect(() => {
    if (!currentImage?.id) return;

    setRenderedView(activeTool === 'magic-edit' ? 'brush' : 'compare');
    brush.resetBrush();
  }, [activeTool, brush.resetBrush, currentImage?.id]);

  useEffect(() => {
    if (activeTool === 'magic-edit') {
      setRenderedView('brush');
      return;
    }

    setRenderedView('compare');
    brush.resetBrush();
  }, [activeTool, brush.resetBrush]);

  useEffect(() => {
    let cancelled = false;

    const loadObjectName = async () => {
      if (estateId) {
        try {
          const estate = await getEstate(estateId);
          if (!cancelled) setObjectName(estate.name ?? estate.address?.address ?? '');
        } catch {
          if (!cancelled) setObjectName('');
        }
        return;
      }

      if (projectId) {
        try {
          const project = await getProject(projectId);
          if (!cancelled) setObjectName(project.name ?? '');
        } catch {
          if (!cancelled) setObjectName('');
        }
        return;
      }

      setObjectName('');
    };

    loadObjectName();

    return () => {
      cancelled = true;
    };
  }, [estateId, projectId]);

  return (
    <div className="box-border h-full min-h-0 w-full bg-white p-3">
      <div className="flex h-full overflow-hidden rounded-xl border border-slate-200">
        <aside
          className={cn(
            'w-[320px] shrink-0 border-r border-slate-200',
            mobileNav.showMobileOptionsOnly && 'w-full border-r-0',
            mobileNav.showMobilePreviewChrome && 'hidden'
          )}
        >
          <RenderingsSidebar
            onMagicEditCreate={handleMagicEditCreate}
            isMagicEditCreating={isMagicEditCreating}
            isMobileView={isMobileView}
            onContinue={mobileNav.goToPreview}
            onAiRenderingCreate={startAiRendering}
            isAiRenderingCreating={isAiRenderingLoading}
            canGenerateAiRendering={canGenerateAiRendering}
          />
        </aside>

        <main
          className={cn(
            'flex min-h-0 min-w-0 flex-1 flex-col',
            mobileNav.showMobileOptionsOnly && 'hidden'
          )}
        >
          {mobileNav.showMobilePreviewChrome ? (
            <RenderingsMobileHeader
              onBack={mobileNav.handleBack}
              title={activeTool === 'magic-edit' ? t('magic-edit') : t('ai-rendering')}
            />
          ) : null}
          <div
            className={cn(
              'flex min-h-0 flex-1 flex-col',
              mobileNav.showMobilePreviewChrome &&
                'overflow-y-auto overflow-x-hidden [scrollbar-color:#cbd5e1_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5'
            )}
          >
            <section
              className={cn(
                'border-b border-slate-200',
                currentImage && !showProgress ? 'shrink-0' : 'flex-1'
              )}
            >
              <div
                className={cn(
                  'flex w-full items-center justify-center',
                  currentImage && !showProgress ? 'h-auto' : 'h-full'
                )}
              >
                <div
                  className={cn(
                    'w-full overflow-hidden border-slate-200 bg-white',
                    currentImage && !showProgress ? 'h-auto' : 'h-full'
                  )}
                >
                  <div
                    className={cn(
                      'relative w-full',
                      currentImage && !showProgress ? 'h-auto' : 'h-full'
                    )}
                  >
                    {showProgress ? (
                      <RenderingProgress progress={progress} text={progressText} />
                    ) : currentImage ? (
                      currentImage.renderedImage && renderedView === 'compare' ? (
                        <div className={previewImageContainerClass}>
                          <ImgComparisonSlider
                            className="border-0 outline-none focus:outline-none focus-visible:outline-none"
                            style={{ outline: 'none' }}
                          >
                            <img
                              slot="first"
                              src={currentImage.originalImage}
                              className="h-full w-full bg-white md:w-auto"
                              height="100%"
                              width="100%"
                              style={{ maxHeight: previewImageMaxHeight }}
                              alt="Original"
                            />
                            <img
                              slot="second"
                              src={currentImage.renderedImage}
                              className="h-full w-full md:w-auto"
                              height="100%"
                              width="100%"
                              style={{ maxHeight: previewImageMaxHeight }}
                              alt="Rendered"
                            />
                          </ImgComparisonSlider>
                        </div>
                      ) : (
                        <div className={previewImageContainerClass}>
                          <div className="relative inline-block max-w-full">
                            <img
                              className="max-w-full object-contain"
                              src={currentImage.originalImage}
                              style={{ maxHeight: previewImageMaxHeight }}
                              alt="Original"
                            />

                            {showBrushCanvas ? (
                              <BrushCanvas
                                handleRef={brush.brushRef}
                                enabled={brushCanvasEnabled}
                                tool={brush.tool}
                                brushSize={brushSize}
                                brushColor={brushColor}
                                onHistoryChange={brush.setHistory}
                                onDrawingStart={maskHint.dismiss}
                                layoutKey={`${previewImageMaxHeight}-${mobileNav.showMobilePreviewChrome}`}
                                className="z-10"
                              />
                            ) : null}

                            {showMaskHintBanner ? (
                              <MagicEditMaskHintBanner onDismiss={maskHint.dismiss} />
                            ) : null}
                          </div>
                        </div>
                      )
                    ) : (
                      <UploadArea
                        inputRef={upload.inputRef}
                        onFileSelect={upload.setImageFile}
                        onOpenFilePicker={upload.openFilePicker}
                      />
                    )}

                    {showMagicEditToolbars ? (
                      isMobileView ? (
                        <div className="absolute inset-x-4 top-4 z-20 flex items-center justify-between">
                          <div className="flex shrink-0">
                            {canSwitchRenderedView ? (
                              <div className="flex gap-1 rounded-sm border border-slate-200 bg-white p-1 shadow-sm">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  onClick={showRenderedBrush}
                                  aria-label={t('brush')}
                                  className={magicEditToolbarToggleButtonClass(
                                    renderedView === 'brush',
                                    true
                                  )}
                                >
                                  <Paintbrush aria-hidden className="size-3" />
                                </Button>

                                <Button
                                  type="button"
                                  variant="ghost"
                                  onClick={showRenderedCompare}
                                  aria-label={t('compare')}
                                  className={magicEditToolbarToggleButtonClass(
                                    renderedView === 'compare',
                                    true
                                  )}
                                >
                                  <ChevronsLeftRight aria-hidden className="size-3" />
                                </Button>
                              </div>
                            ) : null}
                          </div>

                          <div className="flex shrink-0">
                            {brushCanvasEnabled ? (
                              <MagicEditToolbar
                                isMobileView
                                tool={brush.tool}
                                onToolChange={brush.setTool}
                                brushSize={brushSize}
                                onBrushSizeChange={(value) => {
                                  setBrushSize(clamp(value, 1, 256));
                                }}
                                canUndo={brush.history.canUndo}
                                onUndo={() => brush.brushRef.current?.undo()}
                                onClear={brush.clearBrush}
                              />
                            ) : null}
                          </div>
                        </div>
                      ) : (
                        <>
                          {canSwitchRenderedView ? (
                            <div className="absolute left-4 top-4 z-20 flex rounded-sm border border-slate-200 bg-white p-1 shadow-sm">
                              <Button
                                type="button"
                                variant="ghost"
                                onClick={showRenderedBrush}
                                className={cn(
                                  'flex h-8 items-center gap-2 rounded-sm px-3 text-sm font-semibold',
                                  renderedView === 'brush'
                                    ? cn(
                                        'bg-primary-light text-white',
                                        magicEditToolbarSelectedButtonClass
                                      )
                                    : 'text-slate-500 hover:bg-slate-50'
                                )}
                              >
                                <Paintbrush aria-hidden className="size-4" />
                                <span>{t('brush')}</span>
                              </Button>

                              <Button
                                type="button"
                                variant="ghost"
                                onClick={showRenderedCompare}
                                className={cn(
                                  'flex h-8 items-center gap-2 rounded-sm px-3 text-sm font-semibold',
                                  renderedView === 'compare'
                                    ? cn(
                                        'bg-primary-light text-white',
                                        magicEditToolbarSelectedButtonClass
                                      )
                                    : 'text-slate-500 hover:bg-slate-50'
                                )}
                              >
                                <ChevronsLeftRight aria-hidden className="size-4" />
                                <span>{t('compare')}</span>
                              </Button>
                            </div>
                          ) : null}

                          {brushCanvasEnabled ? (
                            <MagicEditToolbar
                              className="absolute right-4 top-4 z-20"
                              tool={brush.tool}
                              onToolChange={brush.setTool}
                              brushSize={brushSize}
                              onBrushSizeChange={(value) => {
                                setBrushSize(clamp(value, 1, 256));
                              }}
                              canUndo={brush.history.canUndo}
                              onUndo={() => brush.brushRef.current?.undo()}
                              onClear={brush.clearBrush}
                            />
                          ) : null}
                        </>
                      )
                    ) : null}
                  </div>
                </div>
              </div>
            </section>

            <GeneratedImagesSection
              images={generatedImages}
              selectedImageId={currentImage?.id}
              onSelectImage={setSelectedPreviewImage}
              onUseAsSource={handleUseAsSource}
              onDownloadImage={handleDownloadImage}
              onEditImage={handleEditImage}
            />

            {mobileNav.showMobilePreviewChrome &&
            activeTool === 'magic-edit' &&
            !isRenderingInProgress ? (
              <MagicEditInstructions className="shrink-0 border-0 px-4 py-3" />
            ) : null}
          </div>

          {mobileNav.showMobilePreviewChrome && !isRenderingInProgress ? (
            <RenderingsMobilePreviewActions
              onChange={mobileNav.handleChange}
              onCreate={handlePreviewCreate}
              createDisabled={previewCreateDisabled}
              createLoading={previewCreateLoading}
            />
          ) : null}
        </main>
      </div>
    </div>
  );
};
